import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Library, BookOpen, Users, Video, Compass, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import {
  contentAPI,
  modulesAPI,
  usersAPI,
  explainerVideosAPI,
  virtualToursAPI,
} from '../../services/api';
import './GlobalSearch.css';

const MAX_PER_GROUP = 5;

const unwrapList = (res) => {
  const payload = res?.data?.data ?? res?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const GlobalSearch = () => {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [groups, setGroups] = useState([]);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const latestQueryRef = useRef('');

  const canSearchUsers = hasPermission(currentUser, PERMISSIONS.VIEW_ALL_USERS);
  const canSearchContent = hasPermission(currentUser, PERMISSIONS.VIEW_ALL_CONTENT);
  const canSearchVideos = hasPermission(currentUser, PERMISSIONS.VIEW_ALL_EXPLAINER_VIDEOS);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = useCallback(async (q) => {
    latestQueryRef.current = q;
    setSearching(true);
    try {
      const requests = [
        canSearchContent
          ? contentAPI.getContent({ search: q }).catch(() => null)
          : Promise.resolve(null),
        modulesAPI.getModules({ search: q }).catch(() => null),
        canSearchUsers
          ? usersAPI.getUsers({ search: q }).catch(() => null)
          : Promise.resolve(null),
        canSearchVideos
          ? explainerVideosAPI.getVideos({ search: q }).catch(() => null)
          : Promise.resolve(null),
        virtualToursAPI.getTours({ search: q }).catch(() => null),
      ];
      const [contentRes, modulesRes, usersRes, videosRes, toursRes] = await Promise.all(requests);

      // A newer keystroke superseded this search; drop the stale results
      if (latestQueryRef.current !== q) return;

      const nextGroups = [
        {
          label: 'VR Content',
          icon: Library,
          items: unwrapList(contentRes).slice(0, MAX_PER_GROUP).map((c) => ({
            key: `content-${c.id}`,
            title: c.title,
            subtitle: c.category || '',
            to: `/content-library/${c.id}`,
          })),
        },
        {
          label: 'Modules',
          icon: BookOpen,
          items: unwrapList(modulesRes).slice(0, MAX_PER_GROUP).map((m) => ({
            key: `module-${m.id}`,
            title: m.title,
            subtitle: m.category || m.level || '',
            to: `/modules/${m.id}`,
          })),
        },
        {
          label: 'People',
          icon: Users,
          items: unwrapList(usersRes).slice(0, MAX_PER_GROUP).map((u) => {
            const firstName = u.firstName || u.first_name || '';
            const lastName = u.lastName || u.last_name || '';
            const name = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (u.username || u.email);
            return {
              key: `user-${u.id}`,
              title: name,
              subtitle: u.role || '',
              to: u.role === 'Trainee' && u.username ? `/trainees/${u.username}` : '/enrollment',
            };
          }),
        },
        {
          label: 'Explainer Videos',
          icon: Video,
          items: unwrapList(videosRes).slice(0, MAX_PER_GROUP).map((v) => ({
            key: `video-${v.id}`,
            title: v.title,
            subtitle: v.category || '',
            to: `/explainer-videos/${v.id}`,
          })),
        },
        {
          label: 'Virtual Tours',
          icon: Compass,
          items: unwrapList(toursRes).slice(0, MAX_PER_GROUP).map((t) => ({
            key: `tour-${t.id}`,
            title: t.title,
            subtitle: t.location || t.category || '',
            to: `/virtual-tours/${t.id}`,
          })),
        },
      ].filter((g) => g.items.length > 0);

      setGroups(nextGroups);
    } finally {
      if (latestQueryRef.current === q) setSearching(false);
    }
  }, [canSearchContent, canSearchUsers, canSearchVideos]);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      latestQueryRef.current = trimmed;
      setGroups([]);
      setSearching(false);
      setOpen(trimmed.length > 0);
      return;
    }
    setOpen(true);
    debounceRef.current = setTimeout(() => runSearch(trimmed), 300);
  };

  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    setGroups([]);
    navigate(item.to);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      const first = groups[0]?.items[0];
      if (first) handleSelect(first);
    }
  };

  // Trainees have a short menu already; search is a staff productivity tool
  if (!currentUser || currentUser.role === 'Trainee') return null;

  return (
    <div className="global-search" ref={wrapperRef}>
      <div className="global-search__box">
        <Search size={16} className="global-search__icon" />
        <input
          type="text"
          className="global-search__input"
          placeholder="Search content, modules, people..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (query.trim().length > 0) setOpen(true); }}
          aria-label="Search the LMS"
        />
        {searching && <Loader2 size={14} className="global-search__spinner" />}
      </div>

      {open && (
        <div className="global-search__dropdown">
          {query.trim().length < 2 ? (
            <div className="global-search__hint">Type at least 2 letters to search</div>
          ) : searching && groups.length === 0 ? (
            <div className="global-search__hint">Searching...</div>
          ) : groups.length === 0 ? (
            <div className="global-search__hint">
              No matches for "{query.trim()}". Try a different word.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="global-search__group">
                <div className="global-search__group-label">
                  <group.icon size={13} />
                  <span>{group.label}</span>
                </div>
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="global-search__result"
                    onClick={() => handleSelect(item)}
                  >
                    <span className="global-search__result-title">{item.title}</span>
                    {item.subtitle && (
                      <span className="global-search__result-sub">{item.subtitle}</span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
